import React from 'react';
import { Modal } from 'antd';
import _toStart from 'lodash/startCase';
import { simpleFetch, simplePut } from 'common/utils/http';

const { confirm, error } = Modal;

const makeDeactivateUserUrl = (userId, isDryRun = false) =>
  `/api/v1/people/${userId}/deactivate${
    isDryRun
      ? '?dry_run=true'
      : ''
  }`;

const getUserFullName = async userId => {
  try {
    const user = await simpleFetch(`/api/v1/people/${userId}?fields=name`);

    return {
      userId,
      userFullName: _toStart(
        user.genres[0].sections[0].attribute_types[0]
          .attributes[0].value.toLowerCase()
      ),
    };
  } catch (e) {
    e.userId = userId;
    throw e;
  }
};

const tryDeactivate = async ({ userId, userFullName }, isDryRun = false) => {
  try {
    return await simplePut(makeDeactivateUserUrl(userId, isDryRun));
  } catch (e) {
    e.userId = userId;
    e.userFullName = userFullName;
    throw e;
  }
};

const showDeactivateUserError = e => {
  error({
    title: `Error with deactivating user ${e.userFullName || 'without name'}`,
    content: (
      <div>
        <p><b>Received {e.code} error for user id {e.userId}:</b></p>
        <p>{e.error}</p>
      </div>
    ),
    maskClosable: true,
  });
};

const showConfirm = ({
  setEditUser,
  onSuccess,
  userItem,
}) => {
  confirm({
    title: `Are you sure you want to deactivate ${userItem.userFullName}?`,
    maskClosable: true,
    onOk: async () => {
      try {
        await tryDeactivate(userItem);
        setEditUser(false);
        onSuccess();
      } catch (e) {
        showDeactivateUserError(e);
      }
    },
  });
};

const showAllConfirm = ({
  setEditUser,
  onSuccess,
  userIdAndFullNamesArr,
}) => {
  confirm({
    title: `Are you sure you want to deactivate all ${userIdAndFullNamesArr.length} selected users?`,
    content: (
      <div>
        <ul>
          {userIdAndFullNamesArr.map(({ userId, userFullName }) => (
            <li key={userId}>{userFullName}</li>
          ))}
        </ul>
      </div>
    ),
    maskClosable: true,
    onOk: async () => {
      try {
        // Deactivate all selected users until finished, or throws error
        await Promise.all(userIdAndFullNamesArr.map(userItem =>
          tryDeactivate(userItem))
        );

        setEditUser(false);
        onSuccess();
      } catch (e) {
        showDeactivateUserError(e);
      }
    },
  });
};

const deactivateUser = async (
  setEditUser,
  selectedUsers,
  onSuccess
) => {
  try {
    if (selectedUsers.length === 1) {
      const userId = selectedUsers[0];
      const userItem = await getUserFullName(userId);

      await tryDeactivate(userItem, true);

      showConfirm({
        setEditUser,
        onSuccess,
        userItem,
      });
    } else {
      const userIdAndFullNamesArr = [];

      // Get all users' full names to confirm who will be deleted, or throw error
      await Promise.all(selectedUsers.map(userId =>
        userIdAndFullNamesArr.push(getUserFullName(userId))
      ));

      // Deactivate dry run for all selected users, or throw error
      await Promise.all(userIdAndFullNamesArr.map(userItem =>
        tryDeactivate(userItem, true)
      ));

      showAllConfirm({
        setEditUser,
        onSuccess,
        userIdAndFullNamesArr,
      });
    }
  } catch (e) {
    showDeactivateUserError(e);
  }
};

export default deactivateUser;
